import * as React from 'react';
import { getGetEnquiryQueryKey, getListEnquiriesQueryKey, useGetEnquiry, useListEnquiries, type CrmAudience } from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EnquiryOpportunityJourney } from '@/components/crm/enquiry-opportunity-journey';
import { lastPageOffset, useNativeListState } from '@/components/interaction/interaction-scope';
import { ListPagination, QueryFeedback } from '@/components/interaction/query-feedback';

interface AgentEnquiryJourneyProps {
  actionId: string;
  audience: CrmAudience | 'all';
  disabled: boolean;
  onContinue: (id: string) => void;
}

/** Native read-only guidance. Conversion remains with its existing native action owner. */
export function AgentEnquiryJourney({ actionId, audience, disabled, onContinue }: AgentEnquiryJourneyProps) {
  const [open, setOpen] = React.useState(false);
  return <div className="mt-2 min-w-0">
    <Button type="button" size="sm" variant="outline" disabled={disabled} aria-expanded={open} onClick={() => setOpen(value => !value)}>
      {open ? 'Hide enquiry journey' : 'Show enquiry journey'}
    </Button>
    {open && !disabled && <EnquiryPicker key={actionId + ':' + audience} actionId={actionId} audience={audience} onContinue={onContinue} />}
  </div>;
}

function EnquiryPicker({ actionId, audience, onContinue }: Omit<AgentEnquiryJourneyProps, 'disabled'>) {
  const searchId = React.useId();
  const [list, update] = useNativeListState('agent-enquiry-journey:' + actionId + ':' + audience, {
    search: '', filters: {}, sort: '', offset: 0, pageSize: 10
  });
  const params = { search: list.search || undefined, limit: list.pageSize, offset: list.offset,
    ...(audience === 'all' ? {} : { audience }) };
  const results = useListEnquiries(params, { query: { queryKey: getListEnquiriesQueryKey(params), retry: false } });
  React.useEffect(() => {
    if (results.isSuccess && !results.isFetching && list.offset > lastPageOffset(results.data.total, list.pageSize))
      update({ offset: lastPageOffset(results.data.total, list.pageSize) });
  }, [results.isSuccess, results.isFetching, results.data, list.offset, list.pageSize]);
  return <section aria-label="Choose enquiry journey" className="mt-3 min-w-0 space-y-3">
    <p className="text-xs text-muted-foreground">Select an exact enquiry to inspect current progress. Review qualification and confirm conversion on the enquiry screen.</p>
    <div className="space-y-1.5"><Label htmlFor={searchId}>Find an enquiry</Label>
      <Input id={searchId} maxLength={200} value={list.search} onChange={event => update({ search: event.target.value }, true)} />
    </div>
    {list.selectedId ? <SelectedEnquiry key={list.selectedId} id={list.selectedId} onContinue={onContinue} onBack={() => update({ selectedId: undefined })} />
      : results.isLoading ? <QueryFeedback state="loading" title="Loading enquiries…" />
      : results.isError ? <QueryFeedback state="error" title="Enquiries are unavailable." description="Your search has been kept." onRetry={() => { void results.refetch(); }} />
      : results.data ? <>
        {results.data.items.length ? <ul className="space-y-2">{results.data.items.map(enquiry => <li key={enquiry.id}>
          <Button className="h-auto w-full justify-start whitespace-normal break-words text-left" variant="outline" size="sm" onClick={() => update({ selectedId: enquiry.id })}>
            {enquiry.name} · {enquiry.subject}
          </Button>
        </li>)}</ul> : <p className="text-sm">No enquiries match this search.</p>}
        <ListPagination label="Enquiry journey pages" offset={list.offset} pageSize={list.pageSize} total={results.data.total} pending={results.isFetching}
          onChange={offset => update({ offset })} />
      </> : null}
  </section>;
}

function SelectedEnquiry({ id, onContinue, onBack }: { id: string; onContinue: (id: string) => void; onBack: () => void }) {
  const enquiry = useGetEnquiry(id, { query: { queryKey: getGetEnquiryQueryKey(id), retry: false } });
  return <div className="min-w-0 space-y-3">
    <Button type="button" size="sm" variant="ghost" onClick={onBack}>Choose another enquiry</Button>
    {enquiry.isError ? <QueryFeedback state="error" title="This enquiry is unavailable." description="It may have changed or you may no longer have access." onRetry={() => { void enquiry.refetch(); }} />
      : enquiry.isLoading || !enquiry.data ? <QueryFeedback state="loading" title="Loading enquiry…" />
      : <>
        <EnquiryOpportunityJourney enquiry={enquiry.data} />
        <Button type="button" size="sm" onClick={() => onContinue(id)}>Continue this enquiry on screen</Button>
        <p className="text-xs text-muted-foreground">Resume the same record. Opening it does not convert it or create an opportunity.</p>
      </>}
  </div>;
}
